import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { customFetch } from '../apiClient';

const MedBrandForm = () => {
  const [form, setForm] = useState({ bname: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [brands, setBrands] = useState([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editBrand, setEditBrand] = useState(null);
  const [editForm, setEditForm] = useState({ bname: "" });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  const formRef = useRef(null);
  const editFormRef = useRef(null);
  const navigate = useNavigate();

  // Auto-dismiss alerts
  useEffect(() => {
    if (success) { const timer = setTimeout(() => setSuccess(""), 3000); return () => clearTimeout(timer); }
  }, [success]);

  useEffect(() => {
    if (error) { const timer = setTimeout(() => setError(""), 4000); return () => clearTimeout(timer); }
  }, [error]);

  useEffect(() => {
    if (editError) { const timer = setTimeout(() => setEditError(""), 4000); return () => clearTimeout(timer); }
  }, [editError]);

  useEffect(() => {
    loadBrands();
    if (formRef.current) {
      const firstInput = formRef.current.querySelector('input:not([disabled]):not([type="hidden"])');
      firstInput?.focus();
    }
  }, []);

  const loadBrands = async () => {
    try {
      const res = await customFetch(`/MedBrandDT`);
      if (res.ok) setBrands(await res.json());
    } catch (err) { console.error(err); }
  };

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  // Keyboard Navigation
  const handleKeyDown = useCallback((e) => {
    const focusableElements = formRef.current?.querySelectorAll('input:not([disabled]), button:not([disabled])');
    if (!focusableElements || focusableElements.length === 0) return;
    const currentIndex = Array.from(focusableElements).indexOf(e.target);

    if (e.key === 'Enter' && currentIndex === focusableElements.length - 1) {
      e.target.closest('form').requestSubmit(); return;
    }
    if (['Enter', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const nextIndex = Math.min(focusableElements.length - 1, currentIndex + 1);
      focusableElements[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = Math.max(0, currentIndex - 1);
      focusableElements[prevIndex]?.focus();
    }
  }, []);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (formRef.current && e.target.closest('form') === formRef.current) handleKeyDown(e);
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleKeyDown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bname?.trim()) { setError("Brand Name is required."); return; }
    setLoading(true); setError(""); setSuccess("");

    try {
      const formData = new FormData();
      formData.append("BName", form.bname.trim());

      const res = await customFetch(`/MedBrandDT`, {
        method: "POST", body: formData
      });

      if (!res.ok) throw new Error(await res.text());
      setSuccess("Medicine Brand added successfully!"); setForm({ bname: "" }); loadBrands();
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const openEditModal = (brand) => {
    setEditBrand(brand); setEditForm({ bname: brand.bName || "" });
    setEditError(""); setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false); setEditBrand(null); setEditForm({ bname: "" }); setEditError("");
  };

  const handleEditChange = (e) => setEditForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.bname?.trim()) { setEditError("Brand Name is required"); return; }
    setEditLoading(true);

    try {
      const formData = new FormData();
      formData.append("BName", editForm.bname.trim());

      const res = await customFetch(`/MedBrandDT/${editBrand.bid}`, {
        method: "PUT", body: formData
      });

      if (!res.ok) throw new Error(await res.text());
      loadBrands(); setSuccess("Updated Successfully"); closeEditModal();
    } catch (err) { setEditError(err.message); } finally { setEditLoading(false); }
  };

  const deleteBrand = async (id) => {
    if (!window.confirm("Delete this Medicine Brand?")) return;
    try {
      const res = await customFetch(`/MedBrandDT/${id}`, {
        method: "DELETE"
      });
      if (!res.ok) throw new Error(await res.text());
      setBrands(prev => prev.filter(b => b.bid !== id)); setSuccess("Deleted Successfully");
    } catch (err) { setError(err.message); }
  };

  return (
    <>
      <div className="medbrand-container">
        <div className="medbrand-card">
          {/* Header */}
          <div className="medbrand-header">
            <div className="header-left">
              <div className="header-icon"><i className="bi bi-bookmark-star"></i></div>
              <div className="header-text"><h5>Medicine Brand Master</h5><p>Add and manage brands</p></div>
            </div>
            <div className="header-actions">
              <button type="button" onClick={() => navigate('/MedBrandDTList')} className="btn-header-action" title="View List">
                <i className="bi bi-list-ul"></i> <span>View List</span>
              </button>
            </div>
          </div>

          <div className="card-content">
            {error && <div className="alert-custom alert-danger"><i className="bi bi-exclamation-circle-fill"></i> {error}</div>}
            {success && <div className="alert-custom alert-success"><i className="bi bi-check-circle-fill"></i> {success}</div>}

            {/* Add Form (Compacted to Inline Row) */}
            <div className="form-section">
              <div className="section-title"><i className="bi bi-plus-circle"></i><span>Add New Brand</span></div>
              <div className="section-content">
                <form ref={formRef} onSubmit={handleSubmit}>
                  <div className="add-form-row">
                    <div className="form-group">
                      <label className="form-label"><i className="bi bi-bookmark"></i> Brand Name <span className="req">*</span></label>
                      <input 
                        name="bname" value={form.bname} onChange={handleChange} 
                        placeholder="e.g. Cipla" className="form-input" 
                        required disabled={loading} 
                      />
                    </div>
                    <button type="submit" className="btn-submit" disabled={loading}>
                      {loading ? "Saving..." : <><i className="bi bi-plus-lg"></i> Add Brand</>}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* List Table */}
            <div className="list-section">
              <div className="section-title"><i className="bi bi-list-ul"></i><span>Brands List ({brands.length})</span></div>
              <div className="section-content p-0">
                <div className="table-responsive">
                  <table className="custom-table">
                    <thead>
                      <tr>
                        <th className="col-sr">Sr No</th>
                        <th>Brand Name</th>
                        <th className="col-actions text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brands.length === 0 ? (
                        <tr><td colSpan="3" className="empty-state">No brands found.</td></tr>
                      ) : (
                        brands.map((brand, index) => (
                          <tr key={brand.bid}>
                            <td className="col-sr text-muted fw-600">{index + 1}</td>
                            <td className="fw-500">{brand.bName}</td>
                            <td className="col-actions text-center">
                              <div className="btn-group-actions">
                                <button className="btn-icon btn-edit" onClick={() => openEditModal(brand)} title="Edit"><i className="bi bi-pencil-square"></i></button>
                                <button className="btn-icon btn-delete" onClick={() => deleteBrand(brand.bid)} title="Delete"><i className="bi bi-trash"></i></button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <>
          <div className="modal-overlay" onClick={closeEditModal}></div>
          <div className="modal-container">
            <div className="modal-card">
              <div className="modal-header">
                <div className="modal-title"><i className="bi bi-pencil-square"></i><span>Edit Brand</span></div>
                <button className="btn-modal-close" onClick={closeEditModal}><i className="bi bi-x-lg"></i></button>
              </div>
              <form ref={editFormRef} onSubmit={saveEdit}>
                <div className="modal-body">
                  {editError && <div className="alert-custom alert-danger mb-2"><i className="bi bi-exclamation-circle-fill"></i> {editError}</div>}
                  
                  <div className="form-group mb-2">
                    <label className="form-label">Brand Name <span className="req">*</span></label>
                    <input name="bname" value={editForm.bname} onChange={handleEditChange} className="form-input" required disabled={editLoading} />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Brand ID</label>
                    <input className="form-input readonly" value={editBrand?.bid || ''} readOnly />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-modal-cancel" onClick={closeEditModal} disabled={editLoading}>Cancel</button>
                  <button type="submit" className="btn-modal-submit" disabled={editLoading}>
                    {editLoading ? "Updating..." : "Update Brand"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* COMPACT STYLES */}
      <style jsx>{`
        .medbrand-container { padding: 0; font-family: system-ui, -apple-system, sans-serif; }
        .medbrand-card { background: white; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1); border: 1px solid #e2e8f0; overflow: hidden; }

        /* Header */
        .medbrand-header { background: var(--theme-color, #0088cc); padding: 8px 12px; display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #00d4aa; flex-wrap: wrap; gap: 8px; }
        .header-left { display: flex; align-items: center; gap: 10px; color: white; }
        .header-icon { width: 28px; height: 28px; background: rgba(255,255,255,0.2); border-radius: 4px; display: flex; justify-content: center; align-items: center; font-size: 14px; }
        .header-text h5 { margin: 0; font-size: 14px; font-weight: 600; }
        .header-text p { margin: 0; font-size: 10px; color: rgba(255,255,255,0.85); }
        
        .btn-header-action { padding: 4px 10px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.3); background: rgba(255,255,255,0.15); color: white; font-size: 11px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 5px; transition: 0.2s; }
        .btn-header-action:hover { background: white; color: var(--theme-color, #0088cc); }

        .card-content { padding: 12px; }

        /* Alerts */
        .alert-custom { margin-bottom: 10px; padding: 6px 10px; border-radius: 4px; font-size: 11px; font-weight: 500; display: flex; align-items: center; gap: 6px; }
        .alert-danger { background: #fff5f5; color: #e53e3e; border: 1px solid #feb2b2; }
        .alert-success { background: #f0fff4; color: #27ae60; border: 1px solid #9ae6b4; }

        /* Sections */
        .form-section, .list-section { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; overflow: hidden; margin-bottom: 12px; }
        .section-title { background: var(--theme-color, #3182ce); padding: 6px 10px; color: white; font-size: 12px; font-weight: 600; display: flex; align-items: center; gap: 6px; }
        .section-content { padding: 10px; background: white; }
        .p-0 { padding: 0 !important; }

        /* Add Form Inline */
        .add-form-row { display: flex; gap: 10px; align-items: flex-end; }
        .form-group { flex: 1; display: flex; flex-direction: column; gap: 4px; }
        .mb-2 { margin-bottom: 8px; }
        .mb-3 { margin-bottom: 12px; }
        .form-label { font-size: 11px; font-weight: 600; color: #4a5568; display: flex; align-items: center; gap: 4px;}
        .form-label i { color: var(--theme-color, #3182ce); }
        .req { color: #e53e3e; }
        
        .form-input { width: 100%; padding: 6px 8px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 12px; outline: none; transition: 0.2s; background: white; height: 30px;}
        .form-input:focus { border-color: var(--theme-color, #3182ce); box-shadow: 0 0 0 1px var(--theme-color, #3182ce); }
        .readonly { background: #f1f5f9; color: #94a3b8; cursor: not-allowed; }
        
        .btn-submit { padding: 0 16px; border: none; background: var(--theme-color, #3182ce); color: white; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; transition: 0.2s; height: 30px; white-space: nowrap; display: flex; align-items: center; gap: 4px;}
        .btn-submit:hover:not(:disabled) { opacity: 0.9; }
        .btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }

        /* Table */
        .table-responsive { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 11px; text-align: left; }
        .custom-table th { background: #edf2f7; color: #4a5568; padding: 6px 10px; font-weight: 600; border-bottom: 1px solid #cbd5e0; white-space: nowrap; text-transform: uppercase; }
        .custom-table td { padding: 6px 10px; border-bottom: 1px solid #e2e8f0; color: #2d3748; vertical-align: middle; }
        .custom-table tr:hover { background: #f8fafc; }
        
        .col-sr { width: 50px; text-align: center; }
        .col-actions { width: 80px; }
        .fw-500 { font-weight: 500; }
        .fw-600 { font-weight: 600; }
        .text-center { text-align: center; }
        .text-muted { color: #64748b; }
        
        .btn-group-actions { display: flex; justify-content: center; gap: 4px; }
        .btn-icon { width: 24px; height: 24px; border: none; border-radius: 3px; display: inline-flex; justify-content: center; align-items: center; cursor: pointer; font-size: 12px; transition: 0.2s; }
        .btn-edit { background: #ebf8ff; color: #3182ce; }
        .btn-edit:hover { background: #bee3f8; }
        .btn-delete { background: #fff5f5; color: #e53e3e; }
        .btn-delete:hover { background: #fed7d7; }
        .empty-state { text-align: center; padding: 20px; color: #94a3b8; font-style: italic; }

        /* Modal Compact */
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1040; animation: fadeIn 0.2s; }
        .modal-container { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 1050; width: 90%; max-width: 350px; animation: slideUp 0.2s; }
        .modal-card { background: white; border-radius: 6px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.15); }
        
        .modal-header { background: var(--theme-color, #3182ce); padding: 8px 12px; color: white; display: flex; justify-content: space-between; align-items: center; }
        .modal-title { font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 6px; }
        .btn-modal-close { background: none; border: none; color: white; cursor: pointer; font-size: 14px; opacity: 0.8; display: flex; align-items: center; justify-content: center;}
        .btn-modal-close:hover { opacity: 1; }
        
        .modal-body { padding: 12px; }
        .modal-footer { padding: 8px 12px; background: #f8fafc; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 6px; }
        
        .btn-modal-cancel { padding: 6px 12px; border: none; background: #edf2f7; color: #4a5568; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; }
        .btn-modal-cancel:hover { background: #e2e8f0; }
        .btn-modal-submit { padding: 6px 12px; border: none; background: var(--theme-color, #3182ce); color: white; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; }
        .btn-modal-submit:hover:not(:disabled) { opacity: 0.9; }

        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translate(-50%, -40%); } to { opacity: 1; transform: translate(-50%, -50%); } }

        /* Responsive */
        @media (max-width: 600px) {
          .add-form-row { flex-direction: column; align-items: stretch; }
          .btn-submit { width: 100%; justify-content: center; }
        }
      `}</style>
    </>
  );
};



export default MedBrandForm;